/**
 * Boîte à outils — morceaux de figures SVG partagés : quadrillage gradué,
 * poignée déplaçable, flèche de vecteur, marque d'angle droit.
 * Couleurs par variables CSS du thème (clair et sombre).
 */

import type { KeyboardEvent, ReactNode, SVGProps } from 'react';
import { formatTick, niceStep, ticks } from '@jeraisonne/ui';
import type { Frame } from './frame';

export const INK = 'var(--color-ink-700)';
export const MUTED = 'var(--color-ink-400)';
export const GRID = 'var(--color-ink-100)';
export const AXIS = 'var(--color-ink-400)';
/**
 * Fond des figures : un texte posé sur la grille garde un liseré de cette
 * couleur (cf. HaloText). La figure pose la variable avec FIGURE_BG_CLASS.
 */
export const FIGURE_BG_CLASS =
  'bg-white ocean:bg-ink-50 [--fig-bg:#fff] ocean:[--fig-bg:var(--color-ink-50)]';

function tickText(v: number): string {
  return formatTick(v).replace('.', ',').replace('-', '−');
}

/**
 * Quadrillage, axes et graduations de la fenêtre du repère. `step` impose le
 * pas (sinon un pas « rond » selon la largeur) ; `labels=false` les masque.
 */
export function PlotGrid({
  frame,
  step,
  labels = true,
  axes = true,
}: {
  frame: Frame;
  step?: number;
  labels?: boolean;
  axes?: boolean;
}) {
  const { vp, size, tx, ty } = frame;
  const stepX = step ?? niceStep(vp.xMax - vp.xMin);
  const stepY = step ?? niceStep(vp.yMax - vp.yMin);
  const xs = ticks(vp.xMin, vp.xMax, stepX);
  const ys = ticks(vp.yMin, vp.yMax, stepY);
  const axisX = axes && vp.yMin <= 0 && vp.yMax >= 0 ? ty(0) : null;
  const axisY = axes && vp.xMin <= 0 && vp.xMax >= 0 ? tx(0) : null;
  return (
    <g aria-hidden>
      {xs.map((x) => (
        <line key={`gx${x}`} x1={tx(x)} y1={0} x2={tx(x)} y2={size} stroke={GRID} strokeWidth={1} />
      ))}
      {ys.map((y) => (
        <line key={`gy${y}`} x1={0} y1={ty(y)} x2={size} y2={ty(y)} stroke={GRID} strokeWidth={1} />
      ))}
      {axisX !== null && (
        <line x1={0} y1={axisX} x2={size} y2={axisX} stroke={AXIS} strokeWidth={1.25} />
      )}
      {axisY !== null && (
        <line x1={axisY} y1={0} x2={axisY} y2={size} stroke={AXIS} strokeWidth={1.25} />
      )}
      {labels &&
        xs
          .filter((x) => x !== 0 && tx(x) > 12 && tx(x) < size - 12)
          .map((x) => (
            <text
              key={`tx${x}`}
              x={tx(x)}
              y={Math.min(size - 4, (axisX ?? size - 16) + 14)}
              textAnchor="middle"
              fontSize={12}
              fill="var(--color-ink-500)"
            >
              {tickText(x)}
            </text>
          ))}
      {labels &&
        ys
          .filter((y) => y !== 0 && ty(y) > 14 && ty(y) < size - 4)
          .map((y) => (
            <text
              key={`ty${y}`}
              x={Math.max(4, (axisY ?? 4) + 4)}
              y={ty(y) - 3}
              fontSize={12}
              fill="var(--color-ink-500)"
            >
              {tickText(y)}
            </text>
          ))}
    </g>
  );
}

/**
 * Point déplaçable : halo (zone de prise visible), point plein, nom. Le focus
 * clavier allume un anneau ; `keyProps` vient de `useDrag`.
 */
export function DragHandle({
  x,
  y,
  color,
  name,
  label,
  labelDx = 10,
  labelDy = -10,
  active = false,
  keyProps,
}: {
  x: number;
  y: number;
  color: string;
  /** nom pour le lecteur d'écran : « Point B (3 ; 4) » */
  name: string;
  /** lettre affichée à côté du point */
  label?: string;
  labelDx?: number;
  labelDy?: number;
  active?: boolean;
  keyProps: { tabIndex: number; onKeyDown: (e: KeyboardEvent<SVGGElement>) => void };
}) {
  return (
    <g
      role="button"
      aria-roledescription="point déplaçable"
      aria-label={`${name}. Flèches pour le déplacer.`}
      className="cursor-grab outline-none [&:focus-visible_.drag-ring]:opacity-100"
      {...keyProps}
    >
      <circle cx={x} cy={y} r={17} fill={color} opacity={active ? 0.22 : 0.12} />
      <circle
        className="drag-ring"
        cx={x}
        cy={y}
        r={21}
        fill="none"
        stroke="var(--color-primary-400)"
        strokeWidth={2.5}
        opacity={0}
      />
      <circle cx={x} cy={y} r={7} fill={color} />
      {label && (
        <HaloText
          x={x + labelDx}
          y={y + labelDy}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={15}
          fontWeight={700}
          fill={INK}
          style={{ pointerEvents: 'none' }}
        >
          {label}
        </HaloText>
      )}
    </g>
  );
}

/** Pointe de flèche (triangle) au bout du segment (x1, y1) → (x2, y2), en px. */
export function arrowHeadPoints(x1: number, y1: number, x2: number, y2: number, len = 12): string {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const w = len * 0.5;
  const bx = x2 - len * Math.cos(a);
  const by = y2 - len * Math.sin(a);
  const p1 = `${(bx + w * Math.sin(a)).toFixed(2)},${(by - w * Math.cos(a)).toFixed(2)}`;
  const p2 = `${(bx - w * Math.sin(a)).toFixed(2)},${(by + w * Math.cos(a)).toFixed(2)}`;
  return `${x2.toFixed(2)},${y2.toFixed(2)} ${p1} ${p2}`;
}

/** Vecteur dessiné : trait + pointe ; rien si le vecteur est nul. */
export function VectorArrow({
  x1,
  y1,
  x2,
  y2,
  color,
  width = 3,
  dashed = false,
  inset = 0,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  width?: number;
  dashed?: boolean;
  /** recule la pointe de quelques px (pour qu'elle ne passe pas sous un point) */
  inset?: number;
}) {
  const full = Math.hypot(x2 - x1, y2 - y1);
  if (full < 1) return null;
  if (inset > 0 && full > inset * 2) {
    x2 -= ((x2 - x1) / full) * inset;
    y2 -= ((y2 - y1) / full) * inset;
  }
  const len = Math.hypot(x2 - x1, y2 - y1);
  if (len < 1) return null;
  const head = Math.min(13, len * 0.45);
  const a = Math.atan2(y2 - y1, x2 - x1);
  // le trait s'arrête sous la pointe pour qu'elle reste nette
  const ex = x2 - head * 0.8 * Math.cos(a);
  const ey = y2 - head * 0.8 * Math.sin(a);
  return (
    <g>
      <line
        x1={x1}
        y1={y1}
        x2={ex}
        y2={ey}
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
        strokeDasharray={dashed ? '6 5' : undefined}
      />
      <polygon points={arrowHeadPoints(x1, y1, x2, y2, head)} fill={color} />
    </g>
  );
}

/** Petit carré d'angle droit au sommet (px) entre les directions u et v (px). */
export function RightAngleMark({
  x,
  y,
  ux,
  uy,
  vx,
  vy,
  size = 11,
  color = INK,
}: {
  x: number;
  y: number;
  ux: number;
  uy: number;
  vx: number;
  vy: number;
  size?: number;
  color?: string;
}) {
  const nu = Math.hypot(ux, uy) || 1;
  const nv = Math.hypot(vx, vy) || 1;
  const ax = (ux / nu) * size;
  const ay = (uy / nu) * size;
  const bx = (vx / nv) * size;
  const by = (vy / nv) * size;
  return (
    <path
      d={`M ${x + ax} ${y + ay} L ${x + ax + bx} ${y + ay + by} L ${x + bx} ${y + by}`}
      fill="none"
      stroke={color}
      strokeWidth={1.5}
    />
  );
}

/**
 * Arc d'angle au sommet (vx, vy) entre les directions des points (ax, ay) et
 * (bx, by), rayon r, tout en px : le petit arc, dans le bon sens.
 */
export function angleArcPath(
  vx: number,
  vy: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  r: number,
): string {
  const a1 = Math.atan2(ay - vy, ax - vx);
  const a2 = Math.atan2(by - vy, bx - vx);
  let delta = a2 - a1;
  while (delta <= -Math.PI) delta += 2 * Math.PI;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  const x1 = vx + r * Math.cos(a1);
  const y1 = vy + r * Math.sin(a1);
  const x2 = vx + r * Math.cos(a2);
  const y2 = vy + r * Math.sin(a2);
  const f = (n: number) => n.toFixed(2);
  return `M ${f(x1)} ${f(y1)} A ${r} ${r} 0 0 ${delta > 0 ? 1 : 0} ${f(x2)} ${f(y2)}`;
}

/** Texte lisible même posé sur un trait : liseré de la couleur du fond. */
export function HaloText({
  children,
  ...props
}: SVGProps<SVGTextElement> & { children: ReactNode }) {
  return (
    <text
      {...props}
      stroke="var(--fig-bg, #fff)"
      strokeWidth={4}
      strokeLinejoin="round"
      style={{ paintOrder: 'stroke', ...(props.style ?? {}) }}
    >
      {children}
    </text>
  );
}
